import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { DebugDialogComponent } from '../components/debug-dialog/debug-dialog.component';
import { isHostDevOrLocal } from '../common';

export interface DebugLogEntry {
    t: number;         // timestamp
    msg: string;       // message
}

/**
 * Debug Service - Collects debug messages for easy troubleshooting
 *
 * **IMPORTANT: Only works in DEV/LOCAL environments for security!**
 * In production, all methods become no-ops (do nothing).
 *
 * This service allows you to:
 * - Start/stop debug collection with toastr notifications
 * - Collect specific debug messages (filter out console noise)
 * - Show all collected messages in a dialog with easy copy/paste
 * - Optional localStorage persistence for debugging across page refreshes
 *
 * Usage:
 * ```
 * // Start debugging
 * this.debugService.startDebug('AI Scene Generation');
 *
 * // Start with localStorage persistence (survives page refresh)
 * this.debugService.startDebug('AI Scene Generation', { persistToLocalStorage: true });
 *
 * // Insert debug messages
 * this.debugService.insertDebug('Step 1: Scene received');
 * this.debugService.insertDebug('Response:', response);
 *
 * // Stop and show results
 * this.debugService.stopDebug();  // Shows dialog automatically
 *
 * // Load previous session logs (if persisted)
 * this.debugService.loadPreviousSession();
 * this.debugService.showDebug();
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class DebugService {
    private isHostDevOrLocal: boolean;
    private isDebugging = false;
    private debugMessages: string[] = [];
    private debugTitle = 'Debug Session';
    private debugStartTime: number | null = null;
    private persistToLocalStorage = false;
    private storageKey = 'debugLogs';

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService
    ) {
        // CRITICAL: Only enable debugging in dev/local environments
        this.isHostDevOrLocal = isHostDevOrLocal();

        if (!this.isHostDevOrLocal) {
            console.log('[DebugService] Production mode detected - debugging disabled');
        }
    }

    /**
     * Start a new debug session
     * Clears previous messages and shows toastr notification
     * @param title Optional title for this debug session
     * @param options Optional configuration: { persistToLocalStorage: boolean }
     */
    startDebug(title?: string, options?: { persistToLocalStorage?: boolean }): void {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        this.isDebugging = true;
        this.debugMessages = [];
        this.debugTitle = title || 'Debug Session';
        this.debugStartTime = Date.now();
        this.persistToLocalStorage = options?.persistToLocalStorage || false;

        this.toastr.info(
            `Debug collection started: ${this.debugTitle}`,
            'Debug Started',
            { timeOut: 2000 }
        );

        console.log('═══════════════════════════════════════════════════════════');
        console.log(`[DebugService] 🐛 DEBUG STARTED: ${this.debugTitle}`);
        console.log(`[DebugService]    Persist to localStorage: ${this.persistToLocalStorage}`);
        console.log('═══════════════════════════════════════════════════════════');
    }

    /**
     * Stop debug session and show collected messages in dialog
     * Shows toastr notification with message count
     */
    stopDebug(): void {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        if (!this.isDebugging) {
            console.warn('[DebugService] Debug not running - nothing to stop');
            return;
        }

        const elapsed = this.debugStartTime ? Date.now() - this.debugStartTime : 0;
        const elapsedSeconds = (elapsed / 1000).toFixed(2);

        this.isDebugging = false;

        // Save to localStorage if enabled
        if (this.persistToLocalStorage) {
            this.saveToLocalStorage();
        }

        console.log('═══════════════════════════════════════════════════════════');
        console.log(`[DebugService] 🐛 DEBUG STOPPED: ${this.debugTitle}`);
        console.log(`[DebugService]    Messages collected: ${this.debugMessages.length}`);
        console.log(`[DebugService]    Elapsed time: ${elapsedSeconds}s`);
        if (this.persistToLocalStorage) {
            console.log(`[DebugService]    ✓ Saved to localStorage`);
        }
        console.log('═══════════════════════════════════════════════════════════');

        this.toastr.success(
            `Collected ${this.debugMessages.length} debug messages (${elapsedSeconds}s)`,
            'Debug Stopped',
            { timeOut: 3000 }
        );

        // Automatically show dialog
        this.showDebug();
    }

    /**
     * Insert a debug message into the collection
     * Only collects if debugging is active
     * @param messages Variable number of messages (strings or objects)
     */
    insertDebug(...messages: any[]): void {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        if (!this.isDebugging) {
            return; // Silently ignore if not debugging
        }

        const timestamp = this.getTimestamp();
        const formattedMessages = messages.map(msg => {
            if (typeof msg === 'string') {
                return msg;
            } else if (typeof msg === 'object') {
                try {
                    return JSON.stringify(msg, null, 2);
                } catch (e) {
                    return String(msg);
                }
            } else {
                return String(msg);
            }
        }).join(' ');

        const debugLine = `[${timestamp}] ${formattedMessages}`;
        this.debugMessages.push(debugLine);

        // Also log to console for immediate visibility
        console.log(`[DebugService] ${debugLine}`);

        // Auto-save to localStorage if enabled (on every insert)
        if (this.persistToLocalStorage) {
            this.saveToLocalStorage();
        }
    }

    /**
     * Show debug dialog with all collected messages
     * Can be called manually or automatically by stopDebug()
     */
    showDebug(): void {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        if (this.debugMessages.length === 0) {
            this.toastr.warning(
                'No debug messages collected. Use insertDebug() to collect messages.',
                'No Debug Data',
                { timeOut: 3000 }
            );
            return;
        }

        const dialogRef = this.dialog.open(DebugDialogComponent, {
            width: '90vw',
            maxWidth: '1200px',
            height: '80vh',
            data: {
                title: this.debugTitle,
                messages: this.debugMessages,
                startTime: this.debugStartTime,
                endTime: Date.now()
            }
        });
    }

    /**
     * Clear all collected messages without stopping debug session
     * Also clears localStorage if persist was enabled
     */
    clearDebug(): void {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        this.debugMessages = [];

        // Clear localStorage if it was being used
        if (this.persistToLocalStorage) {
            localStorage.removeItem(this.storageKey);
            console.log('[DebugService] Debug messages cleared (including localStorage)');
        } else {
            console.log('[DebugService] Debug messages cleared');
        }
    }

    /**
     * Check if debugging is currently active
     */
    isActive(): boolean {
        return this.isHostDevOrLocal && this.isDebugging;
    }

    /**
     * Get count of collected messages
     */
    getMessageCount(): number {
        if (!this.isHostDevOrLocal) return 0;
        return this.debugMessages.length;
    }

    /**
     * Get all collected messages as array
     */
    getMessages(): string[] {
        if (!this.isHostDevOrLocal) return [];
        return [...this.debugMessages];
    }

    /**
     * Get all collected messages as single string (for copy/paste)
     */
    getMessagesAsString(): string {
        if (!this.isHostDevOrLocal) return '';
        return this.debugMessages.join('\n');
    }

    /**
     * Load debug logs from previous session (if saved to localStorage)
     * Useful for debugging issues that happened before a page refresh
     */
    loadPreviousSession(): boolean {
        if (!this.isHostDevOrLocal) {
            return false;
        }

        try {
            const data = localStorage.getItem(this.storageKey);
            if (data) {
                const logs: DebugLogEntry[] = JSON.parse(data);
                this.debugMessages = logs.map(l => l.msg);
                this.debugTitle = 'Previous Session (Loaded from localStorage)';
                this.debugStartTime = logs.length > 0 ? logs[0].t : Date.now();

                console.log(`[DebugService] ✓ Loaded ${logs.length} messages from previous session`);
                this.toastr.info(
                    `Loaded ${logs.length} debug messages from previous session`,
                    'Previous Session Loaded',
                    { timeOut: 3000 }
                );
                return true;
            }
            return false;
        } catch (e) {
            console.error('[DebugService] Could not load previous session logs:', e);
            return false;
        }
    }

    /**
     * Save current debug messages to localStorage
     * @private
     */
    private saveToLocalStorage(): void {
        if (!this.isHostDevOrLocal) {
            return;
        }
        try {
            const logs: DebugLogEntry[] = this.debugMessages.map(msg => ({
                t: Date.now(),
                msg: msg
            }));
            localStorage.setItem(this.storageKey, JSON.stringify(logs));
        } catch (e) {
            console.error('[DebugService] Could not save logs to localStorage:', e);
            // Don't show error to user - graceful degradation
        }
    }

    /**
     * Get timestamp in HH:MM:SS.mmm format
     * @private
     */
    private getTimestamp(): string {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const ms = String(now.getMilliseconds()).padStart(3, '0');
        return `${hours}:${minutes}:${seconds}.${ms}`;
    }

    /**
     * Save all scenes as individual JSON files to a user-selected directory
     * Uses File System Access API (Chrome/Edge only)
     * IMPORTANT: Only works in dev/local environments
     *
     * @param playerData The player data containing scenes
     * @param xml2js The xml2js parser (passed from caller)
     */
    async saveAllScenesToFiles(playerData: any, xml2js: any): Promise<void> {
        // CRITICAL: Only work in dev/local environments
        if (!this.isHostDevOrLocal) {
            return; // Silent no-op in production
        }

        if (!playerData?.scenes) {
            console.error('[DebugService] No scenes to save');
            return;
        }

        const scenesData: { [key: string]: any } = {};
        let sceneCount = 0;

        // Collect all scene data
        const promises = Object.keys(playerData.scenes).map((key: string) => {
            return new Promise<void>((resolve) => {
                if (!playerData.scenes[key]) {
                    resolve();
                    return;
                }

                const xmlData = playerData.scenes[key].player_data_value;
                if (playerData.scenes[key]?.change_type === 3) {
                    resolve();
                    return;
                }

                xml2js.parseString(xmlData, { charkey: "_" }, (err: any, sceneData: any) => {
                    if (err || !sceneData) {
                        console.log(`[DebugService] Skipping corrupted scene ${key}`);
                        resolve();
                        return;
                    }

                    scenesData[key] = sceneData;
                    sceneCount++;
                    resolve();
                });
            });
        });

        await Promise.all(promises);
        console.log(`[DebugService] ✓ Collected ${sceneCount} scenes, now saving to files...`);

        this.toastr.info(
            `Collected ${sceneCount} scenes. Select folder to save...`,
            'Scene Export',
            { timeOut: 3000 }
        );

        // Request directory access (File System Access API)
        try {
            // @ts-ignore - File System Access API
            const dirHandle = await window.showDirectoryPicker();

            // Write each scene to a separate file
            let savedCount = 0;
            for (const [key, sceneData] of Object.entries(scenesData)) {
                const fileName = `scene_${key}.json`;
                const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
                const writable = await fileHandle.createWritable();
                const jsonContent = JSON.stringify(sceneData, null, 2);
                await writable.write(jsonContent);
                await writable.close();
                savedCount++;

                // Log progress every 50 files
                if (savedCount % 50 === 0) {
                    console.log(`[DebugService] Saved ${savedCount}/${sceneCount} files...`);
                }
            }

            console.log(`[DebugService] ✓ Successfully saved ${savedCount} scene files!`);
            this.toastr.success(
                `Successfully saved ${savedCount} scene files!`,
                'Scene Export Complete',
                { timeOut: 5000 }
            );

        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('[DebugService] User cancelled directory selection');
                this.toastr.info('Scene export cancelled', 'Cancelled', { timeOut: 2000 });
            } else {
                console.error('[DebugService] Error saving files:', err);
                this.toastr.error(
                    `Error saving files: ${err.message}`,
                    'Export Error',
                    { timeOut: 5000 }
                );
            }
        }
    }
}
